// Re-authentication (D74, spec 04 § 5): a call refused with `reauthentication_required` raises the ReauthSheet; a
// confirmed password retries the call once, a cancel rejects it with the original refusal.
import { ApiError } from '@orca/api-client'
import { useSyncExternalStore } from 'react'
import { errorHooks } from '../../shared/errors/routeError'

type Pending = { resolve: () => void; reject: () => void }
let pending: Pending | undefined
const listeners = new Set<() => void>()
const emit = () => {
  for (const l of listeners) l()
}

const isReauth = (error: unknown) => error instanceof ApiError && error.effectiveCode === 'reauthentication_required'

/** Opens the sheet and settles when the person confirms (resolve) or cancels (reject). */
function ask(): Promise<void> {
  pending?.reject()
  return new Promise<void>((resolve, reject) => {
    pending = {
      resolve: () => {
        pending = undefined
        emit()
        resolve()
      },
      reject: () => {
        pending = undefined
        emit()
        reject()
      },
    }
    emit()
  })
}

/** Runs `call`; on `reauthentication_required` asks for the password and runs it once more. */
export async function withReauth<T>(call: () => Promise<T>): Promise<T> {
  try {
    return await call()
  } catch (error) {
    if (!isReauth(error)) throw error
    try {
      await ask()
    } catch {
      throw error
    }
    return call()
  }
}

export const reauth = {
  confirmed: () => pending?.resolve(),
  cancelled: () => pending?.reject(),
  /** For the error router's hook: a query refused this way raises the sheet without a retry. */
  raise: () => void ask().catch(() => {}),
}

/** Points the error router's hook at the sheet; main.tsx calls it once. */
export function installReauthHook() {
  errorHooks.reauthenticate = reauth.raise
}

export function useReauthOpen(): boolean {
  return useSyncExternalStore(
    (l) => {
      listeners.add(l)
      return () => listeners.delete(l)
    },
    () => pending !== undefined,
  )
}
